// ============================================================================
// FIX-CAPTURE-TRANCHE-2 — question-card strings (EN + LO).
//
// DECLARED DEVIATION from the "always t('key')" rule, with the reason:
// the card language is governed by the COUNTRY flag `aak_capture.tranche2_lao_countries`
// (Adeel ruling: country flags govern LANGUAGE rollout only), resolved server-side by
// get_capture_cards() and returned as `strings: 'en' | 'lo'`. It must NOT follow the
// farmer's UI language on its own, must work offline without the HTTP-loaded locale
// bundle, and every Lao string must be swappable in ONE place after Adeel's Lao review.
// English is always present and is the fallback for any missing Lao key.
//
// ⚠ EVERY LAO STRING BELOW IS UNREVIEWED (standing item: Adeel's Lao review).
// ============================================================================

export type CaptureLang = 'en' | 'lo';

type CaptureStrings = {
  header: string;
  skip: string;
  // FIX-CAPTURE-TRANCHE-2B-W1 (R-CAP-1): the S129 date card is WITHDRAWN — no s129 strings exist.
  na11: {
    qa: string;           // "{event} were reported near you. Did they affect your farm?"
    qb: string;           // "What did you do after the {event}?"
    yes: string;
    no: string;
    events: Record<string, { plural: string; singular: string }>;
    eventFallback: { plural: string; singular: string };
    options: Record<string, string>;
  };
  s058: { q: string; sixPlus: string };
  s052: { q: string; helper: string; fourPlus: string };
};

const en: CaptureStrings = {
  header: 'A quick question',
  skip: 'Skip',
  na11: {
    qa: '{event} were reported near you. Did they affect your farm?',
    qb: 'What did you do after the {event}?',
    yes: 'Yes',
    no: 'No',
    events: {
      'flood-inundation': { plural: 'Floods', singular: 'flood' },
      'drought-onset': { plural: 'Drought conditions', singular: 'drought' },
    },
    eventFallback: { plural: 'Weather problems', singular: 'event' },
    options: {
      'protective-action': 'Used savings, insurance or a loan',
      'help-sought': 'Asked for help',
      'asset-sold': 'Sold animals or equipment',
      'input-cut': 'Cut back on inputs',
      'none-recorded': 'Waited it out',
    },
  },
  s058: { q: 'How many people work on your farm, including you?', sixPlus: '6+' },
  s052: { q: 'How many ways does your household earn money?', helper: 'e.g. crops, livestock, wages, trade', fourPlus: '4+' },
};

// UNREVIEWED — Adeel's Lao review pending on every string in this block.
const lo: CaptureStrings = {
  header: 'ຄຳຖາມສັ້ນໆ',
  skip: 'ຂ້າມ',
  na11: {
    qa: 'ມີລາຍງານ{event}ໃກ້ທ່ານ. ມັນກະທົບຟາມຂອງທ່ານບໍ?',
    qb: 'ຫຼັງ{event} ທ່ານໄດ້ເຮັດຫຍັງ?',
    yes: 'ແມ່ນ',
    no: 'ບໍ່',
    events: {
      'flood-inundation': { plural: 'ນ້ຳຖ້ວມ', singular: 'ນ້ຳຖ້ວມ' },
      'drought-onset': { plural: 'ໄພແຫ້ງແລ້ງ', singular: 'ໄພແຫ້ງແລ້ງ' },
    },
    eventFallback: { plural: 'ໄພທຳມະຊາດ', singular: 'ເຫດການ' },
    options: {
      'protective-action': 'ໃຊ້ເງິນເກັບ, ປະກັນໄພ ຫຼື ກູ້ຢືມ',
      'help-sought': 'ຂໍຄວາມຊ່ວຍເຫຼືອ',
      'asset-sold': 'ຂາຍສັດ ຫຼື ເຄື່ອງມື',
      'input-cut': 'ຫຼຸດການໃຊ້ປັດໄຈການຜະລິດ',
      'none-recorded': 'ລໍຖ້າໃຫ້ຜ່ານໄປ',
    },
  },
  s058: { q: 'ມີຈັກຄົນເຮັດວຽກໃນຟາມຂອງທ່ານ ລວມທັງທ່ານ?', sixPlus: '6+' },
  s052: { q: 'ຄອບຄົວຂອງທ່ານມີລາຍໄດ້ຈາກຈັກທາງ?', helper: 'ເຊັ່ນ: ພືດ, ສັດລ້ຽງ, ຄ່າແຮງ, ການຄ້າ', fourPlus: '4+' },
};

export const CAPTURE_STRINGS: Record<CaptureLang, CaptureStrings> = { en, lo };

export function captureStrings(lang: CaptureLang | string | undefined): CaptureStrings {
  return lang === 'lo' ? lo : en;
}

/** "{event}" interpolation for the coping card, with an English/Lao fallback per event type. */
export function eventWord(s: CaptureStrings, eventType: string | undefined, form: 'plural' | 'singular'): string {
  const e = (eventType && s.na11.events[eventType]) || s.na11.eventFallback;
  return e[form];
}
